/**
 * The persisted profile (FS-10 §3.3, §4.5). `localStorage` is the source of truth (Design Bible
 * §10) — the game is fully playable with no network and no account, forever. Firestore mirroring
 * (FS-10 R20/R21) is FS-16, the backend phase, and is not built; `Profile.uid`/`deviceId` are kept
 * as fields so that phase does not need a schema migration to add them meaningfully later.
 */
import type { HullId } from '../player/hulls/types';
import type { ModeId } from '../game/modes/types';
import { SECTORS } from '../content/sectors';

export const PROFILE_KEY = 'spacewaves.profile.v1';
export const PROFILE_SCHEMA_VERSION = 1;
const LEGACY_PROGRESS_KEY = 'spacewaves.progress.v1';
const SAVE_DEBOUNCE_MS = 2000;
const RECENT_RUNS_CAP = 50;

export interface BestEntry {
  score: number;
  runId: string;
  at: number;
}

export interface LifetimeStats {
  runs: number;
  ticks: number;
  distanceU: number;
  grazes: number;
  hazardsCleared: number;
  overdriveTicks: number;
  overdrives: number;
  bossesDefeated: number;
  deaths: number;
  sectorsCompleted: number;
  dailiesPlayed: number;
  dailiesCompleted: number;
}

export type RunStatKey = 'hazardsCleared' | 'beatSteers' | 'multiplierPeak' | 'sectorIndexCleared';

/** The persisted summary of one finished (or abandoned) run — what unlock evaluation reads (R13). */
export interface RunSummary {
  runId: string;
  at: number; // ms epoch
  modeId: ModeId;
  hullId: HullId;
  sectorIndex: number | null; // custom track / segments beyond the sector list: null
  sectorCleared: number; // highest sector index cleared this run, 0 if none
  score: number;
  won: boolean;
  hazardsCleared: number;
  beatSteers: number;
  hitsTaken: number;
  overdrives: number;
  multiplierPeak: number;
  ranked: boolean;
  abandoned: boolean;
}

export interface Profile {
  schemaVersion: 1;
  unlocked: {
    hulls: HullId[];
    sectors: number[];
    modes: ModeId[];
  };
  /** Best score per `${modeId}:${sectorId}` (Sector Run) or `${modeId}` (other ranked modes). */
  bests: Record<string, BestEntry>;
  lifetime: LifetimeStats;
  recentRuns: RunSummary[];
  /** Custom Track personal bests, keyed by track digest (FS-09 Q6). Never synced. */
  customBests: Record<string, number>;
  daily: {
    lastAttemptUtcDate: string | null;
  };
  /**
   * One-shot gates that are neither unlocks nor stats (Amendment PT-1). Added without a schema
   * bump: an existing v1 profile has no `flags`, and `load()` fills the blank defaults, which is
   * exactly the right answer — a returning player has not been offered How to Play and has not
   * completed an onboarding that did not exist.
   */
  flags: {
    /** FS-12 R36 / FS-01 R41 — offered once, at the end of a profile's first run. */
    howToPlayOffered: boolean;
    /** FS-01 R14 — onboarding runs exactly once per profile, and is re-runnable on demand. */
    onboardingCompleted: boolean;
  };
}

function blankLifetime(): LifetimeStats {
  return {
    runs: 0,
    ticks: 0,
    distanceU: 0,
    grazes: 0,
    hazardsCleared: 0,
    overdriveTicks: 0,
    overdrives: 0,
    bossesDefeated: 0,
    deaths: 0,
    sectorsCompleted: 0,
    dailiesPlayed: 0,
    dailiesCompleted: 0,
  };
}

export function blankProfile(): Profile {
  return {
    schemaVersion: PROFILE_SCHEMA_VERSION,
    unlocked: {
      hulls: ['Vector'],
      sectors: [1],
      modes: ['sector-run', 'infinite', 'zen', 'custom'],
    },
    bests: {},
    lifetime: blankLifetime(),
    recentRuns: [],
    customBests: {},
    daily: { lastAttemptUtcDate: null },
    flags: { howToPlayOffered: false, onboardingCompleted: false },
  };
}

/** Old sector-only progress (pre-FS-10). Folded into a fresh profile on first load. */
function migrateLegacy(profile: Profile): void {
  try {
    const raw = localStorage.getItem(LEGACY_PROGRESS_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { unlocked?: number; best?: Record<string, number>; cleared?: string[] };
    const unlocked = Math.max(1, Math.min(SECTORS.length, parsed.unlocked ?? 1));
    for (let i = 2; i <= unlocked; i++) if (!profile.unlocked.sectors.includes(i)) profile.unlocked.sectors.push(i);
    for (const [sectorId, score] of Object.entries(parsed.best ?? {})) {
      const key = `sector-run:${sectorId}`;
      if (!profile.bests[key] || profile.bests[key]!.score < score) {
        profile.bests[key] = { score, runId: 'legacy', at: 0 };
      }
    }
    if ((parsed.cleared ?? []).length > 0) profile.lifetime.sectorsCompleted = parsed.cleared!.length;
  } catch {
    /* legacy blob unreadable — a fresh profile is correct, not a fault */
  }
}

export class ProfileStore {
  private profile: Profile;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private memoryOnly = false;

  constructor() {
    this.profile = this.load();
  }

  private load(): Profile {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(PROFILE_KEY);
    } catch {
      this.memoryOnly = true;
      return blankProfile();
    }
    if (!raw) {
      const fresh = blankProfile();
      migrateLegacy(fresh);
      return fresh;
    }
    let parsed: Partial<Profile> | null = null;
    try {
      parsed = JSON.parse(raw) as Partial<Profile>;
    } catch {
      this.stashCorrupt(raw);
      return blankProfile();
    }
    if (!parsed || typeof parsed !== 'object') {
      this.stashCorrupt(raw);
      return blankProfile();
    }
    if ((parsed.schemaVersion ?? 0) > PROFILE_SCHEMA_VERSION) {
      // Newer than this build knows about (R19): play on a fresh in-memory profile, never write.
      this.memoryOnly = true;
      if (import.meta.env.DEV) console.warn('profile.version-ahead — playing on an in-memory profile');
      return blankProfile();
    }
    // schemaVersion === CURRENT (no migrations defined yet below it).
    const fresh = blankProfile();
    return {
      ...fresh,
      ...parsed,
      unlocked: {
        hulls: parsed.unlocked?.hulls ?? fresh.unlocked.hulls,
        sectors: parsed.unlocked?.sectors ?? fresh.unlocked.sectors,
        modes: parsed.unlocked?.modes ?? fresh.unlocked.modes,
      },
      lifetime: { ...fresh.lifetime, ...parsed.lifetime },
      bests: parsed.bests ?? {},
      recentRuns: parsed.recentRuns ?? [],
      customBests: parsed.customBests ?? {},
      daily: { ...fresh.daily, ...parsed.daily },
      flags: { ...fresh.flags, ...parsed.flags },
    };
  }

  private stashCorrupt(raw: string): void {
    try {
      localStorage.setItem(`${PROFILE_KEY}-corrupt-${Date.now()}`, raw);
    } catch {
      /* best effort */
    }
    if (import.meta.env.DEV) console.warn('profile.corrupt — starting from a fresh profile');
  }

  get(): Readonly<Profile> {
    return this.profile;
  }

  /** Debounced write (R22). */
  save(): void {
    if (this.memoryOnly) return;
    if (this.saveTimer) return;
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      this.flush();
    }, SAVE_DEBOUNCE_MS);
  }

  /** Immediate write — results, pause, pagehide (R22 triggers). */
  flush(): void {
    if (this.memoryOnly) return;
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    try {
      if (this.profile.recentRuns.length > RECENT_RUNS_CAP) {
        this.profile.recentRuns = this.profile.recentRuns.slice(0, RECENT_RUNS_CAP);
      }
      localStorage.setItem(PROFILE_KEY, JSON.stringify(this.profile));
    } catch {
      /* private browsing / quota — progress is a convenience, not a requirement */
    }
  }

  isUnlocked(kind: 'hull', id: HullId): boolean;
  isUnlocked(kind: 'sector', id: number): boolean;
  isUnlocked(kind: 'mode', id: ModeId): boolean;
  isUnlocked(kind: 'hull' | 'sector' | 'mode', id: HullId | number | ModeId): boolean {
    if (kind === 'hull') return this.profile.unlocked.hulls.includes(id as HullId);
    if (kind === 'sector') return this.profile.unlocked.sectors.includes(id as number);
    return this.profile.unlocked.modes.includes(id as ModeId);
  }

  grantHull(id: HullId): void {
    if (!this.profile.unlocked.hulls.includes(id)) this.profile.unlocked.hulls.push(id);
  }
  grantSector(index: number): void {
    if (!this.profile.unlocked.sectors.includes(index)) this.profile.unlocked.sectors.push(index);
  }
  grantMode(id: ModeId): void {
    if (!this.profile.unlocked.modes.includes(id)) this.profile.unlocked.modes.push(id);
  }

  best(key: string): number {
    return this.profile.bests[key]?.score ?? 0;
  }

  /** Record a finished run's summary, updating bests/lifetime. Returns true if it set a new best. */
  recordRun(s: RunSummary, rankable: boolean): boolean {
    this.profile.recentRuns.unshift(s);
    if (this.profile.recentRuns.length > RECENT_RUNS_CAP) this.profile.recentRuns.length = RECENT_RUNS_CAP;

    const l = this.profile.lifetime;
    l.runs++;
    l.hazardsCleared += s.hazardsCleared;
    l.overdrives += s.overdrives;
    if (!s.won && !s.abandoned) l.deaths++;
    if (s.sectorCleared > 0) l.sectorsCompleted += s.sectorCleared;
    // A boss occupies every segment's Finale in every mode except Custom Track (FS-08 R06,
    // FS-09 §4.1) — the Gauntlet unlock ("defeat any 2 bosses") must count all of them, not just
    // bosses beaten while already playing Gauntlet, or the unlock would be a deadlock.
    if (s.modeId !== 'custom') l.bossesDefeated += s.sectorCleared;
    if (s.modeId === 'daily') {
      l.dailiesPlayed++;
      if (s.won) l.dailiesCompleted++;
    }

    let isBest = false;
    if (rankable) {
      const key = s.sectorIndex != null ? `${s.modeId}:sector-${s.sectorIndex}` : s.modeId;
      if (s.score > this.best(key)) {
        this.profile.bests[key] = { score: Math.floor(s.score), runId: s.runId, at: s.at };
        isBest = true;
      }
    }
    return isBest;
  }

  recordCustomBest(digest: string, score: number): boolean {
    const prev = this.profile.customBests[digest] ?? 0;
    if (score > prev) {
      this.profile.customBests[digest] = Math.floor(score);
      return true;
    }
    return false;
  }

  /** Read a one-shot gate (FS-12 R36, FS-01 R14). */
  flag(key: keyof Profile['flags']): boolean {
    return this.profile.flags[key];
  }

  setFlag(key: keyof Profile['flags'], value: boolean): void {
    if (this.profile.flags[key] === value) return;
    this.profile.flags[key] = value;
    this.save();
  }

  markDailyAttempted(utcDate: string): void {
    this.profile.daily.lastAttemptUtcDate = utcDate;
  }

  hasDailyAttempt(utcDate: string): boolean {
    return this.profile.daily.lastAttemptUtcDate === utcDate;
  }

  erase(): void {
    this.profile = blankProfile();
    try {
      localStorage.removeItem(PROFILE_KEY);
    } catch {
      /* ignore */
    }
  }
}
